import React, { useState, useMemo } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import LocationForm from '../../components/HR/LocationForm';
import { Location } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useLocations, useCreateLocation, useUpdateLocation, useDeleteLocation } from '../../hooks/useLocations';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';

const Locations: React.FC = () => {
  const queryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'address', order: 'asc' as const }]
  }), []);

  const { data: locations = [], isLoading, error, refetch } = useLocations(queryOptions);

  const createMutation = useCreateLocation(() => {
    refetch();
  });

  const updateMutation = useUpdateLocation(() => {
    refetch();
  });

  const deleteMutation = useDeleteLocation(() => {
    refetch();
  });

  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const columns: TableColumn[] = [
    { key: 'address', label: 'Address', sortable: true },
    { key: 'isActive', label: 'Status', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {value ? 'Active' : 'Inactive'}
      </span>
    ) },
    { key: 'createdAt', label: 'Created Date', sortable: true, render: (value: string) =>
      new Date(value).toLocaleDateString()
    },
    { key: 'updatedAt', label: 'Updated Date', sortable: true, render: (value: string) =>
      new Date(value).toLocaleDateString()
    }
  ];

  const filteredLocations = useMemo(() => {
    return locations.filter(location => {
      const matchesSearch = !searchTerm ||
        location.address.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && location.isActive) ||
        (statusFilter === 'inactive' && !location.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [locations, searchTerm, statusFilter]);

  const handleExport = () => {
    const exportData = filteredLocations.map(location => ({
      'Address': location.address,
      'Status': location.isActive ? 'Active' : 'Inactive',
      'Created Date': new Date(location.createdAt).toLocaleDateString(),
      'Updated Date': new Date(location.updatedAt).toLocaleDateString()
    }));
    exportToExcel(exportData, `locations_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredLocations.map(location => ({
      'Address': location.address,
      'Status': location.isActive ? 'Active' : 'Inactive',
      'Created Date': new Date(location.createdAt).toLocaleDateString(),
      'Updated Date': new Date(location.updatedAt).toLocaleDateString()
    }));
    printAsExcel(exportData, 'Locations');
  };

  const handleAdd = () => {
    setEditingLocation(undefined);
    setShowForm(true);
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setShowForm(true);
  };

  const handleDelete = async (location: Location) => {
    if (window.confirm(`Are you sure you want to delete the location "${location.address}"?`)) {
      await deleteMutation.execute(location);
    }
  };

  const handleFormSubmit = async (locationData: Partial<Location>) => {
    try {
      if (editingLocation) {
        const updateData = {
          ...locationData,
          documentId: editingLocation.documentId
        };
        await updateMutation.execute({ id: editingLocation.id, data: updateData });
      } else {
        await createMutation.execute(locationData);
      }
      setShowForm(false);
      setEditingLocation(undefined);
    } catch {
      // Keep form open on error - toast is shown by mutation
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingLocation(undefined);
  };

  const activeLocations = filteredLocations.filter(l => l.isActive);

  const filterConfigs = [
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Location Management"
        description="Manage location addresses for coast guard permits and other location-based operations."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Locations</dt>
                  <dd className="text-lg font-medium text-gray-900">{locations.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Locations</dt>
                  <dd className="text-lg font-medium text-gray-900">{activeLocations.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Inactive Locations</dt>
                  <dd className="text-lg font-medium text-gray-900">{locations.length - activeLocations.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!showForm && (
        <>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search locations..."
            filters={filterConfigs}
            onClearFilters={() => { setSearchTerm(''); setStatusFilter('all'); }}
            totalCount={locations.length}
            filteredCount={filteredLocations.length}
            entityName="locations"
            onAdd={handleAdd}
            addLabel="Add Location"
            onExport={handleExport}
            onPrint={handlePrint}
          />

          <div className="card overflow-hidden">
            {isLoading ? (
              <TableSkeleton />
            ) : error ? (
              <ErrorState
                message={error.message || String(error)}
                onRetry={refetch}
                entityName="locations"
              />
            ) : (
              <Table
                data={filteredLocations}
                columns={columns}
                onEdit={handleEdit}
                onDelete={handleDelete}
                showAddButton={false}
                searchable={false}
              />
            )}
          </div>
        </>
      )}

      {showForm && (
        <LocationForm
          location={editingLocation}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default Locations;
